import React from "react";
import api, { CreateUserAccGroupRequest } from "../../api";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectHasUserPropertySetting,
  selectUser,
  selectUserRole,
} from "../../slices/user";
import { showError } from "../../utils/helpers";
import { selectLabels } from "../../slices/language";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { CreateAccGroupRequest, CreateRequirementTypeRequest } from "../../api";
import IconifyIcon from "../IconifyIcon";
import RequirementTypeFormModal from "../Modals/RequirementTypeFormModal";
import {
  LocalRequirementType,
  RequirementTypeFormDataType,
} from "../../types/localRequirementType";
import {
  LocalizationProvider,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PHOTOS } from "../../utils/photos";
import { AccessGroupsData } from "../../api/models/access-groups";
import { User } from "../../api/models/user-model";
import {
  AccessPointSchedule,
  mapAccessPointSchedule,
} from "../../utils/mapAccessPointSchedule";
import { defaultTimeDayjs, toDayjsHour } from "../../utils/toDayJsHour";
import { applyRestrictedWeekDays } from "../../utils/applyRestrictedWeekDays";
import { HOST_IDS, SECONDARY_USER_IDS } from "../../utils/roles";

const getDayjsTimeMinutes = (value: Dayjs) =>
  value.hour() * 60 + value.minute();

const getLaterExitToHour = (from: Dayjs): Dayjs => {
  if (from.hour() === 23) {
    return from.minute(59).second(0).millisecond(0);
  }
  return from.add(1, "hour").second(0).millisecond(0);
};

const shouldAutoSetExitToHour = (
  from: Dayjs | null,
  to: Dayjs | null,
): boolean => {
  if (!from) return false;
  if (!to) return true;
  // Only reset when equal: earlier "hasta" is valid for overnight ranges
  // (e.g. 9:00 PM → 5:00 AM).
  return getDayjsTimeMinutes(to) === getDayjsTimeMinutes(from);
};

const isSameClockTime = (a: Dayjs, b: Dayjs) =>
  getDayjsTimeMinutes(a) === getDayjsTimeMinutes(b);

interface AccessPointsToShow {
  property: string;
  accessPoints: {
    id: number;
    name: string;
  }[];
}

interface AccessPoint {
  id: number;
  name: string;
}

interface AccessPointsEnd {
  id: number;
  provId: number;
  name: string;
  weekDays: string[];
  startHour: Dayjs | null;
  endHour: Dayjs | null;
  exitFromHour: Dayjs | null;
  exitToHour: Dayjs | null;
  antipassback: string;
  description: string;
  register: boolean;
  photos: string[];
  printable_pass: boolean;
}

interface FixedAccessPointsEnd {
  id: number;
  name: string;
  rules: Rule[];
}

interface Rule {
  provId: number;
  weekDays: string[];
  startHour: Dayjs | null;
  endHour: Dayjs | null;
  exitFromHour: Dayjs | null;
  exitToHour: Dayjs | null;
  antipassback: string;
  description: string;
  register: boolean;
  photos: string[];
  printable_pass: boolean;
  restrictedStartHour: Dayjs | null;
  restrictedEndHour: Dayjs | null;
  restrictedExitFromHour: Dayjs | null;
  restrictedExitToHour: Dayjs | null;
  restrictedWeekDays: string[];
}

interface Role {
  id: number;
  name: string;
}

interface UniqueRole {
  value: number;
  name: string;
}

interface NewAccessGroupFormProps {
  showForm: boolean;
  extraFunc: () => void;
  selectedAccessGroup: AccessGroupsData | null;
  hasUserAccessGroups: boolean;
  currentAccessGroups: AccessGroupsData[];
}

const NewAccessGroupForm = ({
  showForm,
  extraFunc,
  selectedAccessGroup,
  hasUserAccessGroups,
  currentAccessGroups,
}: NewAccessGroupFormProps) => {
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();
  const hasExitRestriction = useAppSelector(
    selectHasUserPropertySetting("schedule_restrictions"),
  );
  const hasPrintablePass = useAppSelector(
    selectHasUserPropertySetting("printable_pass"),
  );
  const hasRequirementsPermission = useAppSelector(
    selectHasUserPropertySetting("requirements_permission"),
  );

  const [unitAccessPoints, setUnitAccessPoints] = React.useState<AccessPointsToShow["accessPoints"]>([]);
  const [unitPointsLoading, setUnitPointsLoading] = React.useState(false);
  const unitDocumentId = user?.unit?.documentId;
  const unitPropertyId = user?.property?.id;

  React.useEffect(() => {
    let active = true;
    setUnitAccessPoints([]);
    if (!hasUserAccessGroups || !unitDocumentId || !unitPropertyId) {
      setUnitPointsLoading(false);
      return;
    }
    setUnitPointsLoading(true);
    api.unitByIdRequest(unitPropertyId, unitDocumentId)
      .then((response) => {
        if (active) setUnitAccessPoints((response.data.access_points ?? []).map(({ id, name }) => ({ id, name })));
      })
      .catch(() => {
        if (active) showError(dispatch, labels.generic_error);
      })
      .finally(() => { if (active) setUnitPointsLoading(false); });
    return () => { active = false; };
  }, [hasUserAccessGroups, unitDocumentId, unitPropertyId, dispatch, labels.generic_error]);

  const availableUnitAccessPoints: AccessPointsToShow = {
    property: user?.property?.name || "",
    accessPoints: unitAccessPoints,
  };

  const generalAccessGroupRoleIds = [...HOST_IDS, ...SECONDARY_USER_IDS];

  const filteredCurrentAccessGroups = currentAccessGroups.filter((item) =>
    item?.role_permissions_users?.some((role) =>
      generalAccessGroupRoleIds.includes(role?.id),
    ),
  );

  const hostAvailableAccessPointIds = availableUnitAccessPoints.accessPoints
    .map((accessPoint) => accessPoint.id)
    .join(",");

  const schedules = mapAccessPointSchedule(filteredCurrentAccessGroups);

  const daysOfWeekArray = [
    {
      label: labels.monday,
      value: "monday",
      disabled: false,
    },
    {
      label: labels.tuesday,
      value: "tuesday",
      disabled: false,
    },
    {
      label: labels.wednesday,
      value: "wednesday",
      disabled: false,
    },
    {
      label: labels.thursday,
      value: "thursday",
      disabled: false,
    },
    {
      label: labels.friday,
      value: "friday",
      disabled: false,
    },
    {
      label: labels.saturday,
      value: "saturday",
      disabled: false,
    },
    {
      label: labels.sunday,
      value: "sunday",
      disabled: false,
    },
  ];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [accessPoints, setAccessPoints] = React.useState<AccessPointsToShow[]>(
    [],
  );
  const [roles, setRoles] = React.useState<UniqueRole[]>([]);
  const [accessGroupName, setAccessGroupName] = React.useState<string>("");
  const [selectedAccessPoints, setSelectedAccessPoints] = React.useState<
    AccessPoint[]
  >([]);
  const [selectedRoles, setSelectedRoles] = React.useState<Role[]>([]);
  const [fixedSelectedAccessPoints, setFixedSelectedAccessPoints] =
    React.useState<AccessPointsEnd[]>([]);
  const [allowAllDayAccess, setAllowAllDayAccess] = React.useState<{
    [key: number]: boolean;
  }>({});
  const [users, setUsers] = React.useState<User[]>([]);
  const [userIds, setUserIds] = React.useState<number[]>([]);
  const [secondaryUsersFetched, setSecondaryUsersFetched] =
    React.useState<boolean>(false);
  const [requirementTypes, setRequirementTypes] = React.useState<
    LocalRequirementType[]
  >([]);
  const [requirementTypeModalOpen, setRequirementTypeModalOpen] =
    React.useState<boolean>(false);
  const [editingRequirementType, setEditingRequirementType] =
    React.useState<LocalRequirementType | null>(null);

  const hostSetupValidations = React.useMemo(() => {
    if (!hasUserAccessGroups || selectedAccessGroup) {
      return [];
    }

    const validations: {
      key: string;
      message: string;
      blocksCreate: boolean;
    }[] = [];

    if (filteredCurrentAccessGroups.length === 0) {
      validations.push({
        key: "no_general_access_groups",
        message: labels.no_general_access_groups_warning,
        blocksCreate: true,
      });
    }

    if (!unitPointsLoading && unitAccessPoints.length === 0) {
      validations.push({
        key: "no_unit_access_points",
        message: labels.no_unit_access_points_warning,
        blocksCreate: true,
      });
    }

    if (!user?.unit?.id) {
      validations.push({
        key: "no_unit_assigned",
        message: labels.no_unit_assigned_warning,
        blocksCreate: true,
      });
    }

    if (
      user?.unit?.id &&
      availableUnitAccessPoints.accessPoints.length > 0 &&
      secondaryUsersFetched &&
      users.length === 0
    ) {
      validations.push({
        key: "no_secondary_users",
        message: labels.no_secondary_users_warning,
        blocksCreate: false,
      });
    }

    return validations;
  }, [
    filteredCurrentAccessGroups.length,
    availableUnitAccessPoints.accessPoints.length,
    hasUserAccessGroups,
    labels.no_general_access_groups_warning,
    unitPointsLoading,
    unitAccessPoints.length,
    labels.no_unit_access_points_warning,
    labels.no_secondary_users_warning,
    labels.no_unit_assigned_warning,
    secondaryUsersFetched,
    selectedAccessGroup,
    user?.unit?.id,
    users.length,
  ]);

  const isHostCreateBlocked = hostSetupValidations.some(
    (validation) => validation.blocksCreate,
  );

  const isAccessGroupFormValid = React.useMemo(() => {
    const hasName = Boolean(accessGroupName.trim());
    const hasAccessPoints = selectedAccessPoints.length > 0;
    const hasRoles = hasUserAccessGroups || selectedRoles.length > 0;
    return hasName && hasAccessPoints && hasRoles;
  }, [
    accessGroupName,
    hasUserAccessGroups,
    selectedAccessPoints.length,
    selectedRoles.length,
  ]);

  const isSubmitDisabled =
    !isAccessGroupFormValid || isHostCreateBlocked || isLoading || unitPointsLoading;

  const handleChange = (event: any, value: AccessPoint[]) => {
    setSelectedAccessPoints(value);
  };

  const handleRoleChange = (event: any, value: Role[]) => {
    setSelectedRoles(value);
  };

  const defaultValues = () => {
    // Al cerrar el modal (showForm false) o al abrir en modo "nuevo" (sin grupo seleccionado), resetear el formulario
    if (!showForm || !selectedAccessGroup) {
      setSelectedAccessPoints([]);
      setAccessGroupName("");
      setFixedSelectedAccessPoints([]);
      setSelectedRoles([]);
      setAllowAllDayAccess({});
      setSecondaryUsersFetched(false);
      setRequirementTypes([]);
      if (!showForm) {
        setTimeout(() => {
          getAccessPoints();
          getUserRoles();
        }, 300);
      } else {
        setAccessPoints([]);
        setRoles([]);
        getAccessPoints();
        getUserRoles();
      }
    }
    if (!showForm || !selectedAccessGroup) {
      setUserIds([]);
    }
  };

  React.useEffect(() => {
    defaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, selectedAccessGroup]);

  const companyId = user?.company?.id;
  const propertyId = user?.property?.id;

  const fixedAccessPoints = accessPoints[0]?.accessPoints?.map((item) => item);
  const fixedRoles = roles.map((item) => {
    return {
      id: item?.value,
      name: item?.name,
    };
  });

  const icon = (
    <IconifyIcon icon={"mdi:checkbox-blank-outline"} style={{ fontSize: 24 }} />
  );
  const checkedIcon = (
    <IconifyIcon icon={"mdi:checkbox-marked"} style={{ fontSize: 24 }} />
  );

  // scheduleByAccessPoint
  const scheduleMap = new Map<number, AccessPointSchedule>(
    schedules.map((s) => [s.access_point, s]),
  );

  let finalAccessPoints: FixedAccessPointsEnd[] = [];

  if (hasUserAccessGroups) {
    finalAccessPoints = selectedAccessPoints.map((item) => {
      const containedData = fixedSelectedAccessPoints.filter(
        (filteredItem) => filteredItem?.id === item?.id,
      );

      const scheduleForThisAP = scheduleMap.get(item.id);

      return {
        id: item.id,
        name: item.name,
        rules: containedData.map((cItem) => {
          return {
            provId: cItem?.provId,
            weekDays: cItem?.weekDays,
            startHour: cItem?.startHour,
            endHour: cItem?.endHour,
            exitFromHour: cItem?.exitFromHour,
            exitToHour: cItem?.exitToHour,
            antipassback: cItem?.antipassback,
            description: cItem?.description,
            register: cItem?.register,
            photos: cItem?.photos,
            printable_pass: hasPrintablePass ? cItem?.printable_pass : false,
            // 👇 aquí ya no metemos strings, metemos Dayjs|null
            restrictedStartHour: scheduleForThisAP
              ? toDayjsHour(scheduleForThisAP.start_time)
              : null,

            restrictedEndHour: scheduleForThisAP
              ? toDayjsHour(scheduleForThisAP.end_time)
              : null,

            restrictedExitFromHour: scheduleForThisAP?.exit_restriction_from
              ? toDayjsHour(scheduleForThisAP.exit_restriction_from)
              : scheduleForThisAP?.exit_restriction
                ? toDayjsHour(scheduleForThisAP.exit_restriction)
                : null,

            restrictedExitToHour: scheduleForThisAP?.exit_restriction_to
              ? toDayjsHour(scheduleForThisAP.exit_restriction_to)
              : null,

            // Only restrict days when the general group defines them.
            // Falling back to the currently selected days caused a loop
            // where picking one day disabled all the others.
            restrictedWeekDays: scheduleForThisAP?.week_days ?? [],
          };
        }),
      };
    });
  } else {
    finalAccessPoints = selectedAccessPoints.map((item) => {
      const containedData = fixedSelectedAccessPoints.filter(
        (filteredItem) => filteredItem?.id === item?.id,
      );
      return {
        id: item?.id,
        name: item?.name,
        rules: containedData.map((cItem) => {
          return {
            provId: cItem?.provId,
            weekDays: cItem?.weekDays,
            startHour: cItem?.startHour,
            endHour: cItem?.endHour,
            antipassback: `${cItem?.antipassback}`,
            description: cItem?.description,
            register: cItem?.register,
            photos: cItem?.photos,
            printable_pass: hasPrintablePass ? cItem?.printable_pass : false,
            exitFromHour: cItem?.exitFromHour,
            exitToHour: cItem?.exitToHour,
            restrictedStartHour: cItem?.startHour,
            restrictedEndHour: cItem?.endHour,
            restrictedExitFromHour: cItem?.exitFromHour,
            restrictedExitToHour: cItem?.exitToHour,
            restrictedWeekDays: cItem?.weekDays,
          };
        }),
      };
    });
  }

  const generateUniqueRandomNumber = (
    existingProvIds: number[],
    min: number,
    max: number,
  ): number => {
    let newRandomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
    while (existingProvIds.includes(newRandomNumber)) {
      newRandomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
    }
    return newRandomNumber;
  };

  React.useEffect(() => {
    if (selectedAccessGroup) {
      setAccessGroupName(selectedAccessGroup?.name);
      setSelectedAccessPoints(
        selectedAccessGroup?.access_points?.map((item) => ({
          id: item?.id,
          name: item?.name,
        })),
      );
      setUserIds(
        selectedAccessGroup?.user_access_groups
          ?.map((item) => item?.user?.id)
          .filter((id): id is number => id !== undefined),
      );
      setSelectedRoles(
        selectedAccessGroup?.role_permissions_users?.map((item) => {
          const baseName = item?.name.replace(/_[a-z]$/, "");
          return {
            id: item?.id,
            name:
              baseName === "host"
                ? labels.user
                : baseName === "secondary_user"
                  ? labels.secondary_user
                  : baseName === "visitor"
                    ? labels.visitor
                    : baseName === "property_manager"
                      ? labels.property_manager
                      : baseName === "admin"
                        ? labels.property_manager
                        : labels.property_manager,
          };
        }),
      );
      const usedProvIds: number[] = [];
      const configsToAdd: AccessPointsEnd[] =
        selectedAccessGroup?.configuration.map((conf) => {
          const uniqueRandomNumber = generateUniqueRandomNumber(
            usedProvIds,
            1,
            100,
          );
          usedProvIds.push(uniqueRandomNumber);
          const start_time = toDayjsHour(conf.start_time ?? null);
          const endTime = toDayjsHour(conf.end_time ?? null);
          const exitFromTime = toDayjsHour(
            conf.exit_restriction_from ?? conf.exit_restriction ?? null,
          );
          const exitToTime = toDayjsHour(conf.exit_restriction_to ?? null);
          return {
            id: conf?.id,
            provId: uniqueRandomNumber,
            name: conf?.name,
            weekDays: conf?.week_days,
            startHour: start_time,
            endHour: endTime,
            exitFromHour: exitFromTime,
            exitToHour: exitToTime,
            antipassback:
              conf?.antipassback === -1 ? "0" : `${conf?.antipassback}`,
            description: conf?.description,
            register: conf?.register,
            photos: conf?.photos,
            printable_pass: hasPrintablePass ? conf?.printable_pass : false,
          };
        });
      setFixedSelectedAccessPoints(configsToAdd);
      if (hasRequirementsPermission) {
        const allowedTypes: RequirementTypeFormDataType[] = [
          "string",
          "boolean",
          "selector",
        ];
        setRequirementTypes(
          selectedAccessGroup?.requirement_types
            ?.filter((item) =>
              allowedTypes.includes(
                item?.data_type as RequirementTypeFormDataType,
              ),
            )
            .map((item) => ({
              localId: `existing-${item.id}`,
              serverId: item.id,
              serverDocumentId: item.documentId,
              name: item?.name ?? "",
              description: item?.description ?? "",
              data_type: item?.data_type as RequirementTypeFormDataType,
              required: item?.required ?? false,
              access_key: false,
              transaction: true,
              selector_data: item?.selector_data ?? [],
            })) ?? [],
        );
      }
    }
  }, [
    hasPrintablePass,
    hasRequirementsPermission,
    labels.property_manager,
    labels.secondary_user,
    labels.user,
    labels.visitor,
    selectedAccessGroup,
  ]);

  React.useEffect(() => {
    if (selectedAccessPoints.length === 0) {
      return;
    }

    const usedProvIds = fixedSelectedAccessPoints.map((item) => item.provId);
    const updatedArray2: AccessPointsEnd[] = [];

    selectedAccessPoints.forEach((item1) => {
      const existingItems = fixedSelectedAccessPoints.filter(
        (item2) => item2.id === item1.id,
      );

      if (existingItems.length > 0) {
        updatedArray2.push(...existingItems);
        return;
      }

      const uniqueRandomNumber = generateUniqueRandomNumber(
        usedProvIds,
        1,
        100,
      );
      usedProvIds.push(uniqueRandomNumber);
      updatedArray2.push({
        id: item1?.id,
        provId: uniqueRandomNumber,
        name: item1?.name,
        weekDays: [],
        startHour: null,
        endHour: null,
        exitFromHour: null,
        exitToHour: null,
        antipassback: "0",
        description: "",
        register: true,
        photos: [],
        printable_pass: false,
      });
    });

    setFixedSelectedAccessPoints(updatedArray2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccessPoints]);

  const onAddRule = (accessPointId: number, accessPointName: string) => {
    const currentAccessPoints = [...fixedSelectedAccessPoints];
    // Genera una lista de todos los provId existentes
    const existingProvIds = currentAccessPoints.map((item) => item.provId);

    // Genera un número aleatorio único
    const uniqueRandomNumber = generateUniqueRandomNumber(
      existingProvIds,
      1,
      100,
    );
    const newRule = {
      id: accessPointId,
      provId: uniqueRandomNumber,
      name: accessPointName,
      weekDays: [],
      startHour: null,
      endHour: null,
      exitFromHour: null,
      exitToHour: null,
      antipassback: "0",
      description: "",
      register: true,
      photos: [],
      printable_pass: false,
    };
    currentAccessPoints.push(newRule);

    setFixedSelectedAccessPoints(currentAccessPoints);
  };

  const onDeleteRule = (ruleId: number) => {
    const dupAP = [...fixedSelectedAccessPoints];
    const fixedRules = dupAP.filter((item) => item?.provId !== ruleId);
    setFixedSelectedAccessPoints(fixedRules);
  };

  React.useEffect(() => {
    getAccessPoints();
    getUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  React.useEffect(() => {
    if (hasUserAccessGroups) {
      setAccessPoints([availableUnitAccessPoints]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasUserAccessGroups,
    hostAvailableAccessPointIds,
    filteredCurrentAccessGroups.length,
  ]);

  React.useEffect(() => {
    getUsersByUnit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.unit?.id, hasUserAccessGroups]);

  const getUsersByUnit = async () => {
    if (user?.unit?.id && hasUserAccessGroups) {
      try {
        setIsLoading(true);
        setSecondaryUsersFetched(false);
        const response = await api.usersByUnitListRequest(
          user?.unit?.id,
          [13, 14],
        );
        if (response) {
          setUsers(response);
        } else {
          setUsers([]);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
        setUsers([]);
      } finally {
        setSecondaryUsersFetched(true);
        setIsLoading(false);
      }
    } else if (hasUserAccessGroups) {
      setUsers([]);
      setSecondaryUsersFetched(true);
    }
  };

  const getUserRoles = async () => {
    if (hasUserAccessGroups) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await api.userRolesRequest();
      if (response) {
        const filteredRoles = response.roles.filter(
          (item) =>
            ["host", "secondary_user", "visitor", "property_admin"].includes(
              item.name.replace(/_[a-z]$/, ""),
            ),
        );
        const uniqueRoles: Record<string, UniqueRole> = {};
        filteredRoles.forEach((role) => {
          const baseName = role.name.replace(/_[a-z]$/, "");
          if (!uniqueRoles[baseName]) {
            uniqueRoles[baseName] = {
              value: role.id,
              name:
                baseName === "host"
                  ? labels.user
                  : baseName === "secondary_user"
                    ? labels.secondary_user
                    : baseName === "visitor"
                      ? labels.visitor
                      : labels.property_manager,
            };
          }
        });

        const fixedResponse = Object.values(uniqueRoles);
        setRoles(fixedResponse);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessPoints = async () => {
    if (hasUserAccessGroups) {
      setAccessPoints([availableUnitAccessPoints]);
    }
    if (userRole === "admin") {
      if (propertyId) {
        try {
          setIsLoading(true);
          const response = await api.accessPointsRequest(propertyId, 1, 100);
          if (response) {
            const fixedResponse = [
              {
                property: user?.property?.name,
                accessPoints: response.data.map((item) => {
                  return {
                    id: item?.id,
                    name: item?.name,
                  };
                }),
              },
            ];
            setAccessPoints(fixedResponse);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setIsLoading(false);
        }
      }
    }
    if (userRole === "ciso") {
      if (companyId) {
        try {
          setIsLoading(true);
          const response = await api.accessPointsByCompanyRequest(companyId);
          if (response) {
            const fixedResponse = response?.data.map((item) => item);
            const accessPointsByProperty = fixedResponse.reduce(
              (acc, item) => {
                const propertyName = item.property?.name;

                // Verificar que propertyAddress no sea undefined y es un string
                if (typeof propertyName === "string") {
                  if (!acc[propertyName]) {
                    acc[propertyName] = [];
                  }
                  acc[propertyName].push({
                    id: item.id,
                    name: item.name,
                  });
                } else {
                  // Manejar el caso en que propertyAddress sea undefined
                  const defaultAddress = "Unknown Property";
                  if (!acc[defaultAddress]) {
                    acc[defaultAddress] = [];
                  }
                  acc[defaultAddress].push({
                    id: item.id,
                    name: item.name,
                  });
                }

                return acc;
              },
              {} as { [key: string]: { id: number; name: string }[] },
            );

            // Paso 2: Convertir el mapa a la estructura de datos deseada
            const result = Object.entries(accessPointsByProperty).map(
              ([property, accessPoints]) => ({
                property,
                accessPoints,
              }),
            );
            setAccessPoints(result);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const getRequirementDataTypeLabel = (
    dataType: RequirementTypeFormDataType,
  ) => {
    if (dataType === "boolean") return labels.requirement_type_boolean;
    if (dataType === "selector") return labels.selector;
    return labels.text;
  };

  const buildRequirementTypePayload = (
    requirement: LocalRequirementType,
    accessGroupId: number,
  ): CreateRequirementTypeRequest => ({
    data: {
      name: requirement.name,
      description: requirement.description,
      data_type: requirement.data_type,
      required: requirement.required,
      access_key: false,
      transaction: true,
      ...(requirement.data_type === "selector"
        ? { selector_data: requirement.selector_data }
        : {}),
      access_groups: [accessGroupId],
      url: "",
    },
  });

  const syncRequirementTypes = async (
    accessGroupId: number,
    requirements: LocalRequirementType[],
    originalServerDocumentIds: string[],
  ) => {
    const currentServerDocumentIds = requirements
      .map((item) => item.serverDocumentId)
      .filter((id): id is string => typeof id === "string");

    for (const serverDocumentId of originalServerDocumentIds) {
      if (!currentServerDocumentIds.includes(serverDocumentId)) {
        await api.deleteRequirementTypeRequest(serverDocumentId);
      }
    }

    for (const requirement of requirements) {
      const payload = buildRequirementTypePayload(requirement, accessGroupId);
      if (requirement.serverDocumentId) {
        await api.updateRequirementTypeRequest(
          requirement.serverDocumentId,
          payload,
        );
      } else {
        await api.createRequirementTypeRequest(payload);
      }
    }
  };

  const handleSaveRequirementType = (requirement: LocalRequirementType) => {
    setRequirementTypes((prev) => {
      const exists = prev.some((item) => item.localId === requirement.localId);
      if (exists) {
        return prev.map((item) =>
          item.localId === requirement.localId ? requirement : item,
        );
      }
      return [...prev, requirement];
    });
  };

  const handleDeleteRequirementType = (localId: string) => {
    setRequirementTypes((prev) =>
      prev.filter((item) => item.localId !== localId),
    );
  };

  const openNewRequirementTypeModal = () => {
    setEditingRequirementType(null);
    setRequirementTypeModalOpen(true);
  };

  const openEditRequirementTypeModal = (requirement: LocalRequirementType) => {
    setEditingRequirementType(requirement);
    setRequirementTypeModalOpen(true);
  };

  const closeRequirementTypeModal = () => {
    setRequirementTypeModalOpen(false);
    setEditingRequirementType(null);
  };

  const onSummit = async () => {
    if (accessGroupName && selectedAccessPoints?.length > 0) {
      const configurationPayload = fixedSelectedAccessPoints.map((item) => {
        const isAllDay = allowAllDayAccess[item?.provId] || false;
        return {
          id: item?.id,
          week_days: item?.weekDays,
          start_time: isAllDay
            ? "00:00:00"
            : dayjs(item?.startHour).format("HH:mm:ss"),
          end_time: isAllDay
            ? "23:59:59"
            : dayjs(item?.endHour).format("HH:mm:ss") === "23:59:00"
              ? "23:59:59"
              : dayjs(item?.endHour).format("HH:mm:ss"),
          exit_restriction_from: isAllDay
            ? undefined
            : item?.exitFromHour
              ? dayjs(item?.exitFromHour).format("HH:mm:ss")
              : undefined,
          exit_restriction_to: isAllDay
            ? undefined
            : item?.exitToHour
              ? dayjs(item?.exitToHour).format("HH:mm:ss")
              : undefined,
          antipassback: Number(item?.antipassback),
          description: item?.description,
          register: item?.register,
          photos: item?.photos,
          printable_pass: hasPrintablePass ? item?.printable_pass : false,
          name: "",
        };
      });
      const payload: CreateAccGroupRequest = {
        data: {
          name: accessGroupName,
          company: companyId ? companyId : null,
          properties: propertyId ? [propertyId] : [],
          access_points: selectedAccessPoints.map((item) => item?.id),
          role_permissions_users: hasUserAccessGroups
            ? [13, 14]
            : selectedRoles.map((item) => item?.id),
          configuration: configurationPayload,
          unit_access_group: hasUserAccessGroups ? true : false,
        },
      };
      try {
        setIsLoading(true);
        const response = await api.createAccessGroupRequest(payload);
        if (response) {
          const accessGroupId = response?.data?.id;

          if (
            hasRequirementsPermission &&
            accessGroupId &&
            requirementTypes.length > 0
          ) {
            await syncRequirementTypes(accessGroupId, requirementTypes, []);
          }

          if (hasUserAccessGroups && users && userIds?.length > 0) {
            for (const user of userIds) {
              const userAccessGroupPayload: CreateUserAccGroupRequest = {
                data: {
                  user: user,
                  access_group: accessGroupId,
                  access: true,
                },
              };
              await api.createUserAccessGroup(userAccessGroupPayload);
            }
          }

          extraFunc();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onUpdate = async () => {
    if (
      accessGroupName &&
      selectedAccessPoints?.length > 0 &&
      selectedAccessGroup
    ) {
      const configurationPayload = fixedSelectedAccessPoints.map((item) => {
        const isAllDay = allowAllDayAccess[item?.provId] || false;
        return {
          id: item?.id,
          week_days: item?.weekDays,
          start_time: isAllDay
            ? "00:00:00"
            : dayjs(item?.startHour).format("HH:mm:ss"),
          end_time: isAllDay
            ? "23:59:59"
            : dayjs(item?.endHour).format("HH:mm:ss") === "23:59:00"
              ? "23:59:59"
              : dayjs(item?.endHour).format("HH:mm:ss"),
          exit_restriction_from: isAllDay
            ? undefined
            : item?.exitFromHour
              ? dayjs(item?.exitFromHour).format("HH:mm:ss")
              : undefined,
          exit_restriction_to: isAllDay
            ? undefined
            : item?.exitToHour
              ? dayjs(item?.exitToHour).format("HH:mm:ss")
              : undefined,
          antipassback: Number(item?.antipassback),
          description: item?.description,
          register: item?.register,
          photos: item?.photos,
          printable_pass: hasPrintablePass ? item?.printable_pass : false,
          name: "",
        };
      });
      const basePayload: CreateAccGroupRequest["data"] = {
        name: accessGroupName,
        company: companyId ? companyId : null,
        properties: propertyId ? [propertyId] : [],
        access_points: selectedAccessPoints.map((item) => item?.id),
        role_permissions_users: hasUserAccessGroups
          ? [13, 14]
          : selectedRoles.map((item) => item?.id),
        unit_access_group: hasUserAccessGroups ? true : false,
      };
      const payload: CreateAccGroupRequest = {
        data: {
          ...basePayload,
          configuration: configurationPayload,
        },
      };
      const originalAccessPointIds = new Set(
        selectedAccessGroup?.access_points?.map(
          (accessPoint) => accessPoint.id,
        ) ?? [],
      );
      const hasNewAccessPoints = selectedAccessPoints.some(
        (accessPoint) => !originalAccessPointIds.has(accessPoint.id),
      );
      const configurationForExistingAccessPoints = configurationPayload.filter(
        (config) => config.id && originalAccessPointIds.has(config.id),
      );
      try {
        setIsLoading(true);
        if (hasNewAccessPoints) {
          await api.updateAccessGroupRequest(selectedAccessGroup?.documentId, {
            data: {
              ...basePayload,
              configuration: configurationForExistingAccessPoints,
            },
          });
        }
        const response = await api.updateAccessGroupRequest(
          selectedAccessGroup?.documentId,
          payload,
        );
        if (response) {
          if (hasRequirementsPermission) {
            const originalRequirementDocumentIds =
              selectedAccessGroup?.requirement_types
                ?.map((item) => item.documentId)
                .filter((id): id is string => typeof id === "string") ?? [];
            await syncRequirementTypes(
              selectedAccessGroup.id,
              requirementTypes,
              originalRequirementDocumentIds,
            );
          }

          // Manejar user_access_groups si hasUserAccessGroups está activo
          if (hasUserAccessGroups) {
            // Obtener los IDs de usuarios actuales del selectedAccessGroup
            const currentUserIds =
              selectedAccessGroup?.user_access_groups
                ?.map((item) => item?.user?.id)
                .filter((id): id is number => id !== undefined) || [];

            // Identificar usuarios nuevos (están en userIds pero no en currentUserIds)
            const usersToAdd = userIds.filter(
              (userId) => !currentUserIds.includes(userId),
            );

            // Identificar usuarios a eliminar (están en currentUserIds pero no en userIds)
            const usersToRemove = currentUserIds.filter(
              (userId) => !userIds.includes(userId),
            );

            // Crear nuevos user_access_groups para usuarios nuevos
            if (usersToAdd.length > 0) {
              for (const userId of usersToAdd) {
                const userAccessGroupPayload: CreateUserAccGroupRequest = {
                  data: {
                    user: userId,
                    access_group: selectedAccessGroup?.id,
                    access: true,
                  },
                };
                await api.createUserAccessGroup(userAccessGroupPayload);
              }
            }

            // Eliminar user_access_groups para usuarios que ya no están
            if (usersToRemove.length > 0) {
              const userAccessGroupsToDelete =
                selectedAccessGroup?.user_access_groups?.filter(
                  (item) =>
                    item?.user?.id !== undefined &&
                    usersToRemove.includes(item?.user?.id),
                ) || [];

              for (const userAccessGroup of userAccessGroupsToDelete) {
                await api.deleteUserAccessGroup(userAccessGroup?.documentId);
              }
            }
          }
          extraFunc();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="border-box flex flex-dir-col gap-4" style={{ width: 660 }}>
      <div
        className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        <div className="w-100 border-box flex flex-dir-col align-items-start gap-10">
          <div className="w-100">
            <p className="t-nowrap body-text-xs mb-3">
              {labels.access_group_name} *
            </p>
            <TextField
              fullWidth
              placeholder={labels.access_group_name}
              value={accessGroupName}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setAccessGroupName(event.target.value);
              }}
            />
          </div>
          {!hasUserAccessGroups && fixedRoles && fixedRoles.length > 0 && (
            <div className="w-100">
              <p className="t-nowrap body-text-xs mb-3">{labels.roles} *</p>
              <Autocomplete
                multiple
                id="checkboxes-tags-demo"
                options={fixedRoles}
                value={selectedRoles}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  );
                }}
                style={{ width: "100%" }}
                renderInput={(params) => <TextField {...params} size="small" />}
                onChange={handleRoleChange}
              />
            </div>
          )}
          {fixedAccessPoints && fixedAccessPoints.length > 0 && (
            <div className="w-100">
              <p className="t-nowrap body-text-xs mb-3">
                {labels.access_points} *
              </p>
              <Autocomplete
                multiple
                id="checkboxes-tags-demo"
                options={fixedAccessPoints}
                value={selectedAccessPoints}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  );
                }}
                style={{ width: "100%" }}
                renderInput={(params) => <TextField {...params} size="small" />}
                onChange={handleChange}
              />
            </div>
          )}
          {finalAccessPoints &&
            finalAccessPoints.length > 0 &&
            finalAccessPoints.map((item, index) => (
              <div
                key={`${index}-${item?.id}`}
                className="w-100 border-box"
                style={{ border: "1px #c4c4c4 solid", borderRadius: 4 }}
              >
                <div className="py-8 px-8 border-box">
                  <p className="t-nowrap body-text-sm font-w-700">
                    {labels.access_point}: {item?.name}
                  </p>
                </div>
                {item?.rules.map((rule, ruleIndex) => {
                  const daysRuleArray = applyRestrictedWeekDays(
                    daysOfWeekArray,
                    rule?.restrictedWeekDays,
                  );

                  // const rFromHour = rule?.restrictedStartHour || undefined;
                  // const rToHour = rule?.restrictedEndHour || undefined;
                  // const rExitHour = rule?.restrictedExitHour || undefined;

                  // const hasNoHoursRestrictions =
                  //   !!rFromHour &&
                  //   !!rToHour &&
                  //   rFromHour.format("HH:mm") === "00:00" &&
                  //   rToHour.format("HH:mm") === "23:59";

                  // const minT =
                  //   !hasUserAccessGroups || hasNoHoursRestrictions
                  //     ? undefined
                  //     : rFromHour;

                  // const maxT =
                  //   !hasUserAccessGroups || hasNoHoursRestrictions
                  //     ? undefined
                  //     : rToHour;

                  // const maxExitT =
                  //   !hasUserAccessGroups || hasNoHoursRestrictions
                  //     ? undefined
                  //     : rExitHour;

                  return (
                    <div key={`${rule?.provId}-${ruleIndex}`}>
                      <Divider />
                      <div className="py-4 px-8 border-box">
                        <div className="border-box flex align-items-center justify-content-between">
                          <p className="t-nowrap body-text-xs mb-3 font-w-500 uppercase">
                            {labels.rule} {ruleIndex + 1}
                          </p>
                          {item.rules.length > 1 && (
                            <IconifyIcon
                              icon={"material-symbols:close"}
                              className="pointer subtitle-color"
                              onClick={() => {
                                onDeleteRule(rule?.provId);
                              }}
                            />
                          )}
                        </div>
                        <div className="w-100">
                          <p className="t-nowrap body-text-xs mb-3">
                            {labels.description}
                          </p>
                          <TextField
                            fullWidth
                            placeholder={labels.access_group_name}
                            value={rule?.description}
                            size="small"
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              const dupAP = [...fixedSelectedAccessPoints];
                              const currentRule = dupAP.filter(
                                (fixAP) => fixAP?.provId === rule?.provId,
                              )[0];
                              const ruleIndex = dupAP.findIndex(
                                (ap) => ap.provId === currentRule.provId,
                              );
                              currentRule.description = event.target.value;
                              dupAP.splice(ruleIndex, 1, currentRule);
                              setFixedSelectedAccessPoints(dupAP);
                            }}
                          />
                        </div>
                        <div
                          className="mt-8"
                          style={{
                            border: hasExitRestriction
                              ? "1px #c4c4c4 solid"
                              : "none",
                            padding: hasExitRestriction ? "10px" : "0px",
                          }}
                        >
                          {hasExitRestriction && (
                            <div className="w-100">
                              <p className="t-nowrap body-text-sm font-w-500 uppercase mb-3">
                                {labels.entrance}
                              </p>
                            </div>
                          )}
                          <div className="w-100 border-box flex align-items-center justify-between gap-8">
                            <div className="w-100">
                              <p className="t-nowrap body-text-xs mb-3">
                                {labels.days_allowed}
                              </p>
                              <div className="w-100 border-box flex align-items-center justify-content-evenly days-main-container">
                                {hasUserAccessGroups ? (
                                  <>
                                    {daysRuleArray.map((day, index) => (
                                      <div
                                        key={`${index}-${day.value}`}
                                        className={`day-container pointer ${
                                          day.disabled ? "disabled-day" : ""
                                        } ${
                                          rule?.weekDays.includes(day.value)
                                            ? "selected"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          if (day.disabled) {
                                            return;
                                          }
                                          const dupAP = [
                                            ...fixedSelectedAccessPoints,
                                          ];
                                          let currentRule = dupAP.filter(
                                            (fixAP) =>
                                              fixAP?.provId === rule?.provId,
                                          )[0];
                                          const ruleIndex = dupAP.findIndex(
                                            (ap) =>
                                              ap.provId === currentRule.provId,
                                          );
                                          const updatedWeekDays = [
                                            ...currentRule.weekDays,
                                          ];
                                          if (
                                            updatedWeekDays.includes(day.value)
                                          ) {
                                            const indexValue =
                                              updatedWeekDays.indexOf(
                                                day.value,
                                              );
                                            updatedWeekDays.splice(
                                              indexValue,
                                              1,
                                            );
                                          } else {
                                            updatedWeekDays.push(day.value);
                                          }
                                          currentRule = {
                                            ...currentRule,
                                            weekDays: updatedWeekDays,
                                          };
                                          dupAP.splice(
                                            ruleIndex,
                                            1,
                                            currentRule,
                                          );
                                          setFixedSelectedAccessPoints(dupAP);
                                        }}
                                      >
                                        <p className="font-13 select-none">
                                          {day.label}
                                        </p>
                                      </div>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {daysOfWeekArray.map((day, index) => (
                                      <div
                                        key={`${index}-${day.value}`}
                                        className={`day-container pointer ${
                                          day.disabled ? "disabled-day" : ""
                                        } ${
                                          rule?.weekDays.includes(day.value)
                                            ? "selected"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          if (day.disabled) {
                                            return;
                                          }
                                          const dupAP = [
                                            ...fixedSelectedAccessPoints,
                                          ];
                                          let currentRule = dupAP.filter(
                                            (fixAP) =>
                                              fixAP?.provId === rule?.provId,
                                          )[0];
                                          const ruleIndex = dupAP.findIndex(
                                            (ap) =>
                                              ap.provId === currentRule.provId,
                                          );
                                          const updatedWeekDays = [
                                            ...currentRule.weekDays,
                                          ];
                                          if (
                                            updatedWeekDays.includes(day.value)
                                          ) {
                                            const indexValue =
                                              updatedWeekDays.indexOf(
                                                day.value,
                                              );
                                            updatedWeekDays.splice(
                                              indexValue,
                                              1,
                                            );
                                          } else {
                                            updatedWeekDays.push(day.value);
                                          }
                                          currentRule = {
                                            ...currentRule,
                                            weekDays: updatedWeekDays,
                                          };
                                          dupAP.splice(
                                            ruleIndex,
                                            1,
                                            currentRule,
                                          );
                                          setFixedSelectedAccessPoints(dupAP);
                                        }}
                                      >
                                        <p className="font-13 select-none">
                                          {day.label}
                                        </p>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="w-50">
                              <p className="t-nowrap body-text-xs mb-3">
                                {labels.from}
                              </p>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  slotProps={{ textField: { size: "small" } }}
                                  value={rule?.startHour ?? defaultTimeDayjs()}
                                  disabled={
                                    allowAllDayAccess[rule?.provId] || false
                                  }
                                  // minTime={minT}
                                  // maxTime={maxT}
                                  onChange={(newValue) => {
                                    const dupAP = [
                                      ...fixedSelectedAccessPoints,
                                    ];
                                    const currentRule = dupAP.filter(
                                      (fixAP) => fixAP?.provId === rule?.provId,
                                    )[0];
                                    const ruleIndex = dupAP.findIndex(
                                      (ap) => ap.provId === currentRule.provId,
                                    );
                                    currentRule.startHour = newValue;
                                    dupAP.splice(ruleIndex, 1, currentRule);
                                    setFixedSelectedAccessPoints(dupAP);
                                  }}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                            <div className="w-50">
                              <p className="t-nowrap body-text-xs mb-3">
                                {labels.to}
                              </p>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  slotProps={{ textField: { size: "small" } }}
                                  value={rule?.endHour ?? defaultTimeDayjs()}
                                  disabled={
                                    allowAllDayAccess[rule?.provId] || false
                                  }
                                  // minTime={minT}
                                  // maxTime={maxT}
                                  onChange={(newValue) => {
                                    const dupAP = [
                                      ...fixedSelectedAccessPoints,
                                    ];
                                    const currentRule = dupAP.filter(
                                      (fixAP) => fixAP?.provId === rule?.provId,
                                    )[0];
                                    const ruleIndex = dupAP.findIndex(
                                      (ap) => ap.provId === currentRule.provId,
                                    );
                                    currentRule.endHour = newValue;
                                    dupAP.splice(ruleIndex, 1, currentRule);
                                    setFixedSelectedAccessPoints(dupAP);
                                  }}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                          </div>
                          <div className="w-100 border-box flex align-items-center justify-between gap-8 mt-8">
                            <div className="flex align-items-center justify-content-start gap-2">
                              <Checkbox
                                checked={
                                  allowAllDayAccess[rule?.provId] || false
                                }
                                onChange={(event) => {
                                  const isChecked = event.target.checked;
                                  setAllowAllDayAccess((prev) => ({
                                    ...prev,
                                    [rule?.provId]: isChecked,
                                  }));

                                  // Si se activa "todo el día", establecer horarios por defecto
                                  if (isChecked) {
                                    const dupAP = [
                                      ...fixedSelectedAccessPoints,
                                    ];
                                    const currentRule = dupAP.filter(
                                      (fixAP) => fixAP?.provId === rule?.provId,
                                    )[0];
                                    const ruleIndex = dupAP.findIndex(
                                      (ap) => ap.provId === currentRule.provId,
                                    );
                                    currentRule.startHour = defaultTimeDayjs();
                                    currentRule.endHour =
                                      toDayjsHour("23:59") ??
                                      dayjs()
                                        .hour(23)
                                        .minute(59)
                                        .second(0)
                                        .millisecond(0);
                                    currentRule.exitFromHour = null;
                                    currentRule.exitToHour = null;
                                    dupAP.splice(ruleIndex, 1, currentRule);
                                    setFixedSelectedAccessPoints(dupAP);
                                  }
                                }}
                              />
                              <p className="body-text-md">
                                {labels.allow_all_day}
                              </p>
                            </div>
                          </div>
                        </div>
                        {hasExitRestriction && (
                          <div
                            className="mt-8"
                            style={{
                              border: hasExitRestriction
                                ? "1px #c4c4c4 solid"
                                : "none",
                              padding: hasExitRestriction ? "10px" : "0px",
                            }}
                          >
                            <div className="w-100 flex align-items-center gap-4 mb-3">
                              <p className="t-nowrap body-text-sm font-w-500 uppercase">
                                {labels.restricted_exit_optional}
                              </p>
                              <Tooltip
                                title={labels.restricted_exit_optional_tooltip}
                                arrow
                                placement="top"
                              >
                                <div>
                                  <IconifyIcon
                                    icon={"material-symbols:info"}
                                    style={{ fontSize: 20, color: "#838383" }}
                                  />
                                </div>
                              </Tooltip>
                            </div>
                            <div className="w-100 border-box flex align-items-center justify-between gap-8">
                              <div className="w-50">
                                <p className="t-nowrap body-text-xs mb-3">
                                  {labels.from}
                                </p>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <TimePicker
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                      },
                                    }}
                                    value={rule?.exitFromHour ?? null}
                                    disabled={
                                      allowAllDayAccess[rule?.provId] || false
                                    }
                                    onChange={(newValue) => {
                                      const dupAP = [
                                        ...fixedSelectedAccessPoints,
                                      ];
                                      const currentRule = dupAP.filter(
                                        (fixAP) =>
                                          fixAP?.provId === rule?.provId,
                                      )[0];
                                      const ruleIndex = dupAP.findIndex(
                                        (ap) =>
                                          ap.provId === currentRule.provId,
                                      );
                                      currentRule.exitFromHour = newValue;
                                      if (
                                        shouldAutoSetExitToHour(
                                          newValue,
                                          currentRule.exitToHour,
                                        ) &&
                                        newValue
                                      ) {
                                        currentRule.exitToHour =
                                          getLaterExitToHour(newValue);
                                      }
                                      dupAP.splice(ruleIndex, 1, currentRule);
                                      setFixedSelectedAccessPoints(dupAP);
                                    }}
                                    viewRenderers={{
                                      hours: renderTimeViewClock,
                                      minutes: renderTimeViewClock,
                                      seconds: renderTimeViewClock,
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>
                              <div className="w-50">
                                <p className="t-nowrap body-text-xs mb-3">
                                  {labels.to}
                                </p>
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <TimePicker
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        fullWidth: true,
                                      },
                                    }}
                                    value={rule?.exitToHour ?? null}
                                    disabled={
                                      allowAllDayAccess[rule?.provId] || false
                                    }
                                    shouldDisableTime={(value, view) => {
                                      if (!rule?.exitFromHour || !value) {
                                        return false;
                                      }
                                      // Allow overnight ranges; only block the exact same time as "desde".
                                      if (view === "minutes") {
                                        return isSameClockTime(
                                          value,
                                          rule.exitFromHour,
                                        );
                                      }
                                      return false;
                                    }}
                                    onChange={(newValue) => {
                                      if (
                                        newValue &&
                                        rule?.exitFromHour &&
                                        isSameClockTime(
                                          newValue,
                                          rule.exitFromHour,
                                        )
                                      ) {
                                        return;
                                      }
                                      const dupAP = [
                                        ...fixedSelectedAccessPoints,
                                      ];
                                      const currentRule = dupAP.filter(
                                        (fixAP) =>
                                          fixAP?.provId === rule?.provId,
                                      )[0];
                                      const ruleIndex = dupAP.findIndex(
                                        (ap) =>
                                          ap.provId === currentRule.provId,
                                      );
                                      currentRule.exitToHour = newValue;
                                      dupAP.splice(ruleIndex, 1, currentRule);
                                      setFixedSelectedAccessPoints(dupAP);
                                    }}
                                    viewRenderers={{
                                      hours: renderTimeViewClock,
                                      minutes: renderTimeViewClock,
                                      seconds: renderTimeViewClock,
                                    }}
                                  />
                                </LocalizationProvider>
                              </div>
                            </div>
                            {(rule?.exitFromHour || rule?.exitToHour) && (
                              <div className="w-100 mt-5 flex justify-content-end">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={
                                    allowAllDayAccess[rule?.provId] || false
                                  }
                                  onClick={() => {
                                    const dupAP = [
                                      ...fixedSelectedAccessPoints,
                                    ];
                                    const currentRule = dupAP.filter(
                                      (fixAP) => fixAP?.provId === rule?.provId,
                                    )[0];
                                    const ruleIndex = dupAP.findIndex(
                                      (ap) => ap.provId === currentRule.provId,
                                    );
                                    currentRule.exitFromHour = null;
                                    currentRule.exitToHour = null;
                                    dupAP.splice(ruleIndex, 1, currentRule);
                                    setFixedSelectedAccessPoints(dupAP);
                                  }}
                                >
                                  {labels.clear_restricted_exit}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        {/* {rule?.antipassback && (
                          <div className="w-100 border-box flex align-items-center justify-between gap-8">
                            <div className="flex align-items-center justify-content-start gap-2">
                              <Tooltip
                                title={labels.allowed_access_tooltip}
                                arrow
                                placement="top-start"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      maxWidth: 250,
                                    },
                                  },
                                }}
                              >
                                <TextField
                                  type="number"
                                  value={rule?.antipassback}
                                  size="small"
                                  style={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    const value =
                                      event.target.value === ""
                                        ? null
                                        : `${Math.max(
                                          0,
                                          Number(event.target.value)
                                        )}`; // Maneja valores vacíos
                                    const dupAP = [
                                      ...fixedSelectedAccessPoints,
                                    ];
                                    const currentRule = dupAP.find(
                                      (fixAP) => fixAP?.provId === rule?.provId
                                    );
                                    if (!currentRule) return; // Asegúrate de que exista la regla actual
                                    const ruleIndex = dupAP.findIndex(
                                      (ap) => ap.provId === currentRule.provId
                                    );
                                    currentRule.antipassback = value
                                      ? value
                                      : "0";
                                    dupAP.splice(ruleIndex, 1, currentRule);
                                    setFixedSelectedAccessPoints(dupAP);
                                  }}
                                />
                              </Tooltip>
                              <p className="body-text-md ml-6">
                                {labels.allowed_access_label}
                              </p>
                            </div>
                          </div>
                        )} */}
                        {hasPrintablePass && (
                          <div className="w-100 border-box flex align-items-center justify-between gap-8 mt-8">
                            <div className="flex align-items-center justify-content-start gap-2">
                              <Checkbox
                                checked={rule?.printable_pass}
                                onChange={(event) => {
                                  const dupAP = [...fixedSelectedAccessPoints];
                                  const currentRule = dupAP.filter(
                                    (fixAP) => fixAP?.provId === rule?.provId,
                                  )[0];
                                  const ruleIndex = dupAP.findIndex(
                                    (ap) => ap.provId === currentRule.provId,
                                  );
                                  currentRule.printable_pass =
                                    event.target.checked;
                                  dupAP.splice(ruleIndex, 1, currentRule);
                                  setFixedSelectedAccessPoints(dupAP);
                                }}
                              />
                              <div className="flex align-items-center gap-2">
                                <p className="body-text-md">
                                  {labels.printable_pass}
                                </p>
                                <Tooltip
                                  title={labels.printable_pass_tooltip}
                                  arrow
                                  placement="top"
                                >
                                  <div>
                                    <IconifyIcon
                                      icon={"material-symbols:info"}
                                      style={{ fontSize: 18, color: "#838383" }}
                                    />
                                  </div>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        )}
                        {rule?.register && (
                          <div className="w-100 border-box flex flex-dir-col align-items-center justify-between mt-8">
                            <div className="w-100">
                              <p className="t-nowrap body-text-xs mb-3">
                                {labels.required_photographs}
                              </p>
                            </div>
                            <div className="w-100 border-box flex flex-dir-col align-items-center justify-between gap-1">
                              {PHOTOS.map((photo, photoIndex) => (
                                <div
                                  key={`${photo.label}-${photoIndex}`}
                                  className="w-100 border-box flex align-items-center justify-content-between px-7 mb-2"
                                  style={{
                                    borderRadius: 4,
                                    border: "1px #e0e0e0 solid",
                                    height: 40,
                                  }}
                                >
                                  <div className="flex align-items-center">
                                    <div
                                      className="flex align-items-center justify-content-center"
                                      style={{ width: 40 }}
                                    >
                                      <IconifyIcon
                                        icon={photo.icon}
                                        className="font-22 subtitle-color"
                                        onClick={() => {}}
                                      />
                                    </div>
                                    <p className="t-nowrap body-text-md">
                                      {photo.label === "id" && labels.id}
                                      {photo.label === "plates" &&
                                        labels.plates}
                                      {photo.label === "visitor" &&
                                        labels.visitor}
                                      {photo.label === "other" &&
                                        labels.equipment}
                                    </p>
                                  </div>
                                  <Switch
                                    checked={rule.photos.includes(photo.label)}
                                    onChange={() => {
                                      const dupAP = [
                                        ...fixedSelectedAccessPoints,
                                      ];
                                      let currentRule = dupAP.filter(
                                        (fixAP) =>
                                          fixAP?.provId === rule?.provId,
                                      )[0];
                                      const ruleIndex = dupAP.findIndex(
                                        (ap) =>
                                          ap.provId === currentRule.provId,
                                      );
                                      const updatedPhotos = [
                                        ...currentRule.photos,
                                      ];
                                      if (updatedPhotos.includes(photo.label)) {
                                        const indexValue =
                                          updatedPhotos.indexOf(photo.label);
                                        updatedPhotos.splice(indexValue, 1);
                                      } else {
                                        updatedPhotos.push(photo.label);
                                      }
                                      currentRule = {
                                        ...currentRule,
                                        photos: updatedPhotos,
                                      };
                                      dupAP.splice(ruleIndex, 1, currentRule);
                                      setFixedSelectedAccessPoints(dupAP);
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <Divider />
                <div className="py-4 px-8 border-box flex justify-content-end">
                  <Button
                    size="small"
                    startIcon={<IconifyIcon icon={"ic:baseline-plus"} />}
                    onClick={() => onAddRule(item?.id, item?.name)}
                  >
                    {labels.new_rule}
                  </Button>
                </div>
              </div>
            ))}
          {hasUserAccessGroups &&
            users &&
            users.length > 0 &&
            finalAccessPoints &&
            finalAccessPoints.length > 0 && (
              <div
                className="w-100 border-box"
                style={{ border: "1px #c4c4c4 solid", borderRadius: 4 }}
              >
                <div className="py-8 px-8 border-box">
                  <p className="t-nowrap body-text-sm font-w-700">
                    {labels.users}
                  </p>
                </div>
                {users.map((user, userIndex) => (
                  <div key={`${user?.id}-${userIndex}`}>
                    <Divider />
                    <div
                      className="py-4 px-8 border-box"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <p className="t-nowrap body-text-md">{user?.name}</p>
                      <Checkbox
                        checked={userIds.includes(user?.id)}
                        onChange={() => {
                          setUserIds((prev) =>
                            prev.includes(user.id)
                              ? prev.filter((id) => id !== user.id)
                              : [...prev, user.id],
                          );
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          {hasRequirementsPermission && (
            <div
              className="w-100 border-box"
              style={{ border: "1px #c4c4c4 solid", borderRadius: 4 }}
            >
              <div className="py-8 px-8 border-box flex align-items-center justify-content-between">
                <p className="t-nowrap body-text-sm font-w-700">
                  {labels.custom_fields}
                </p>
                <Button
                  size="small"
                  startIcon={<IconifyIcon icon={"ic:baseline-plus"} />}
                  onClick={openNewRequirementTypeModal}
                >
                  {labels.add_new_field}
                </Button>
              </div>
              {requirementTypes.length > 0 && (
                <>
                  <Divider />
                  {requirementTypes.map((requirement, requirementIndex) => (
                    <div key={requirement.localId}>
                      {requirementIndex > 0 && <Divider />}
                      <div
                        className="py-4 px-8 border-box flex align-items-center justify-content-between"
                        style={{ gap: 12 }}
                      >
                        <div className="flex flex-dir-col gap-1">
                          <p className="body-text-md font-w-500">
                            {requirement.name}{" "}
                            <span className="body-text-xs title-color">
                              (
                              {getRequirementDataTypeLabel(
                                requirement.data_type,
                              )}
                              )
                            </span>
                          </p>
                          {Boolean(requirement.description?.trim()) && (
                            <p className="body-text-xs title-color">
                              {requirement.description}
                            </p>
                          )}
                          {requirement.data_type === "selector" &&
                            requirement.selector_data.length > 0 && (
                              <p className="body-text-xs title-color">
                                {requirement.selector_data.join(" · ")}
                              </p>
                            )}
                          {requirement.required && (
                            <p className="body-text-xs title-color">
                              {labels.requirement_required}
                            </p>
                          )}
                        </div>
                        <div
                          className="flex align-items-center"
                          style={{ gap: 8 }}
                        >
                          <Tooltip title={labels.edit} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() =>
                                openEditRequirementTypeModal(requirement)
                              }
                              aria-label={labels.edit}
                            >
                              <IconifyIcon icon={"mdi:pencil"} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={labels.delete} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteRequirementType(requirement.localId)
                              }
                              aria-label={labels.delete}
                            >
                              <IconifyIcon icon={"mdi:trash"} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        {hostSetupValidations.map((validation) => (
          <Alert key={validation.key} severity="warning" sx={{ width: "100%" }}>
            {validation.message}
          </Alert>
        ))}
        <div className="w-100" style={{ textAlign: "center" }}>
          <p className="t-nowrap body-text-sm">* {labels.mandatory_fields}</p>
        </div>
        <div className="w-100" style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            disableElevation
            disabled={isSubmitDisabled}
            onClick={
              isSubmitDisabled
                ? () => {}
                : selectedAccessGroup
                  ? onUpdate
                  : onSummit
            }
          >
            {isLoading ? (
              <CircularProgress style={{ color: "white" }} />
            ) : (
              <>
                {selectedAccessGroup
                  ? labels.save_changes
                  : labels.create_access_group}
              </>
            )}
          </Button>
        </div>
      </div>
      <RequirementTypeFormModal
        open={requirementTypeModalOpen}
        onClose={closeRequirementTypeModal}
        onSave={handleSaveRequirementType}
        initialValue={editingRequirementType}
      />
    </div>
  );
};

export default NewAccessGroupForm;
